export const FIELD_GROUPS = [
    {
        id: 'person', label: 'Person', fields: [
            { key: 'person.givenName', label: 'First name', example: 'Jane' },
            { key: 'person.familyName', label: 'Surname', example: 'Citizen' },
            { key: 'person.fullName', label: 'Full name', example: 'Jane Citizen' },
            { key: 'person.address', label: 'Address (one line)', example: '12 Example St, Landsdale WA 6065' },
            { key: 'person.street', label: 'Street address', example: '12 Example St' },
            { key: 'person.suburb', label: 'Suburb', example: 'Landsdale' },
            { key: 'person.state', label: 'State', example: 'WA' },
            { key: 'person.postcode', label: 'Postcode', example: '6065' },
            { key: 'person.phone', label: 'Phone', example: '0400 000 000' },
            { key: 'person.personalEmail', label: 'Personal email', example: 'jane@example.com' },
            { key: 'person.workEmail', label: 'PTI email', example: 'jane.citizen@pti.edu.au' },
            { key: 'person.dateOfBirth', label: 'Date of birth', example: '1 January 1990' },
        ],
    },
    {
        id: 'role', label: 'Position', fields: [
            { key: 'position.title', label: 'Position title', example: 'Trainer and Assessor' },
            { key: 'engagement.type', label: 'Employment type', example: 'permanent full-time' },
            { key: 'engagement.fte', label: 'FTE', example: '1.0' },
            { key: 'employment.hoursPerWeek', label: 'Hours per week', example: '38' },
            { key: 'position.reportsTo', label: 'Reports to', example: 'RTO Manager' },
            { key: 'position.location', label: 'Work location', example: 'Landsdale' },
            { key: 'award.name', label: 'Award or agreement', example: 'Educational Services (Post-Secondary Education) Award 2020' },
            { key: 'award.classification', label: 'Classification', example: 'Level 3' },
        ],
    },
    {
        id: 'pay', label: 'Pay', fields: [
            { key: 'pay.rate', label: 'Pay rate', example: '$45.00', restricted: true },
            { key: 'pay.basis', label: 'Pay basis', example: 'per hour', restricted: true },
            { key: 'pay.rateWithBasis', label: 'Pay rate and basis', example: '$45.00 per hour', restricted: true },
        ],
    },
    {
        id: 'dates', label: 'Dates', fields: [
            { key: 'date.today', label: "Today's date", example: '2 October 2026' },
            { key: 'start.date', label: 'Start date', example: '5 October 2026' },
            { key: 'last.date', label: 'Last day', example: '30 June 2027' },
            { key: 'offer.acceptedDate', label: 'Offer accepted date', example: '3 October 2026' },
            { key: 'offer.respondBy', label: 'Respond-by date (letters of offer)', example: '9 October 2026' },
        ],
    },
    {
        id: 'offer', label: 'Letter of offer', fields: [
            { key: 'offer.conditions', label: 'Offer conditions and terms', example: 'Typed in when the offer is sent' },
        ],
    },
    {
        id: 'signing', label: 'Signatures', fields: [
            { key: 'sign.person', label: 'Signature — employee / contractor', example: 'Their signature goes here', signing: { signer: 'person', kind: 'sign' } },
            { key: 'signdate.person', label: 'Date signed — employee / contractor', example: 'The date they sign', signing: { signer: 'person', kind: 'date' } },
            { key: 'sign.director', label: 'Signature — Director', example: 'Your signature goes here', signing: { signer: 'director', kind: 'sign' } },
            { key: 'signdate.director', label: 'Date signed — Director', example: 'The date you sign', signing: { signer: 'director', kind: 'date' } },
            { key: 'sign.rtom', label: 'Signature — RTO Manager', example: 'Their signature goes here', signing: { signer: 'rtom', kind: 'sign' } },
            { key: 'signdate.rtom', label: 'Date signed — RTO Manager', example: 'The date they sign', signing: { signer: 'rtom', kind: 'date' } },
        ],
    },
    {
        id: 'org', label: 'PTI', fields: [
            { key: 'org.name', label: 'Company name', example: 'Perth Training Institute Pty Ltd' },
            { key: 'org.abn', label: 'ABN', example: '12 345 678 901' },
            { key: 'org.rto', label: 'RTO number', example: '52730' },
            { key: 'org.address', label: 'Company address', example: '5/15 Kalinga Way, Landsdale WA 6065' },
            { key: 'org.phone', label: 'Company phone', example: '1300 65 05 45' },
            { key: 'org.website', label: 'Website', example: 'pti.edu.au' },
            { key: 'org.director', label: 'Director name', example: 'Wayne van Biljon' },
        ],
    },
];
export const FIELD_BY_KEY = Object.fromEntries(FIELD_GROUPS.flatMap((g) => g.fields.map((f) => [f.key, f])));
/** Fields filled from the person record or settings (everything in the catalogue except what is typed per document). */
export const TYPED_FIELDS = new Set(['offer.conditions']);
export const isKnownField = (key) => key in FIELD_BY_KEY;
export const isRestrictedField = (key) => !!FIELD_BY_KEY[key]?.restricted;
export const signingOf = (key) => FIELD_BY_KEY[key]?.signing ?? null;
/** Signature places in a template whose signer isn't in the signing order (they could never be signed). */
export function unsignableFields(keys, signers) {
    return keys.filter((k) => { const s = signingOf(k); return !!s && !signers.includes(s.signer); });
}
/** "custom.probationMonths" → "Probation months"; known fields use their label. */
export function fieldLabel(key) {
    const known = FIELD_BY_KEY[key];
    if (known)
        return known.label;
    const last = key.replace(/^custom\./, '').replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
    return last.charAt(0).toUpperCase() + last.slice(1);
}
/** "Probation period (months)" → "custom.probationPeriodMonths". */
export function customFieldKey(label) {
    const words = label.replace(/[^A-Za-z0-9 ]+/g, ' ').trim().split(/\s+/).filter(Boolean).slice(0, 8);
    if (!words.length)
        return '';
    return 'custom.' + words.map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join('');
}
export const FIELD_RE = /\{\{\s*([\w.]+)\s*\}\}/g;
export function fieldsIn(text) {
    return [...new Set([...text.matchAll(FIELD_RE)].map((m) => m[1]))];
}
/**
 * Guess which field an existing placeholder in a Word document means, e.g. "[Employee Name]" or "«Start_Date»".
 * Returns null when there is no confident match.
 */
export function guessField(placeholder) {
    const t = placeholder.toLowerCase().replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim();
    const has = (...w) => w.every((x) => t.includes(x));
    const who = has('director') ? 'director' : has('manager') || has('rtom') ? 'rtom' : 'person';
    if (has('date') && (has('signed') || has('signature')))
        return `signdate.${who}`;
    if (has('signature') || has('signed') || t === 'sign' || has('sign here'))
        return `sign.${who}`;
    if (has('first', 'name') || has('given', 'name') || t === 'first name')
        return 'person.givenName';
    if (has('surname') || has('last', 'name') || has('family', 'name'))
        return 'person.familyName';
    if (has('name') && (t.includes('employee') || t.includes('full') || t.includes('candidate') || t.includes('contractor') || t === 'name'))
        return 'person.fullName';
    if (has('postcode') || has('post', 'code'))
        return 'person.postcode';
    if (has('suburb'))
        return 'person.suburb';
    if (has('street'))
        return 'person.street';
    if (has('address') && !t.includes('company') && !t.includes('email'))
        return 'person.address';
    if (has('phone') || has('mobile'))
        return 'person.phone';
    if (has('email'))
        return 'person.personalEmail';
    if (has('birth') || t === 'dob')
        return 'person.dateOfBirth';
    if (has('position') || has('job', 'title') || t === 'title' || t === 'role')
        return 'position.title';
    if (has('start', 'date') || has('commencement') || has('commence'))
        return 'start.date';
    if (has('end', 'date') || has('last', 'day') || has('finish'))
        return 'last.date';
    if (has('hours'))
        return 'employment.hoursPerWeek';
    if (has('fte'))
        return 'engagement.fte';
    if (has('classification') || has('level'))
        return 'award.classification';
    if (has('award'))
        return 'award.name';
    if (has('salary') || has('pay') || has('rate') || has('wage') || has('remuneration'))
        return 'pay.rateWithBasis';
    if (has('reports') || has('manager') || has('supervisor'))
        return 'position.reportsTo';
    if (has('location'))
        return 'position.location';
    if (has('date') && (has('today') || t === 'date' || has('letter')))
        return 'date.today';
    if (has('employment', 'type') || has('engagement'))
        return 'engagement.type';
    return null;
}
/** Where a document fits in the staff lifecycle. Used to group templates and each person's documents. */
export const DOC_STAGES = [
    { key: 'recruit', label: 'Recruitment' },
    { key: 'offer', label: 'Offer of employment' },
    { key: 'prestart', label: 'Pre-start' },
    { key: 'onboarding', label: 'Onboarding & induction' },
    { key: 'active', label: 'During employment' },
    { key: 'performance', label: 'Performance & development' },
    { key: 'separation', label: 'Separation' },
];
export const DOC_STAGE_LABEL = Object.fromEntries(DOC_STAGES.map((s) => [s.key, s.label]));
//# sourceMappingURL=fields.js.map